import styled from '@emotion/styled';
import { Button, TableColumnsType, Typography, Image as AntdImage } from 'antd';
import ScrollTable, { ScrollTableProps } from 'components/ScrollTable';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import dayjs from 'lib/dayjs';
import { useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { CategoryMap } from '../hooks/useCaseCategory';

const Tag = styled.span`
  display: inline-block;
  line-height: 26px;
  padding: 0 8px;
  border: solid 1px;
  border-radius: 2px;
`;

const BlueTag = styled(Tag)`
  color: #336fff;
  border-color: #d6e8ff;
`;

const OrangeTag = styled(Tag)`
  color: #f2a100;
  border-color: #faedd2;
`;

const DetailButton = styled(Button)`
  padding: 0;
`;

export const Image = styled(AntdImage)<{ height?: string }>`
  height: ${({ height = '40px' }) => height};
  width: auto;
`;

export const getCategoryLabel = (
  caseBasicInfo: Feedback.CaseBasicInfo,
  categoryMap: CategoryMap,
  systemLanguage: General.SystemLanguage,
) => {
  const { caseType, caseCategory } = caseBasicInfo;

  if (caseType === 'ACTIVITY') {
    return '活动反馈';
  }

  const {
    [caseType]: {
      [caseCategory]: { label: { [systemLanguage]: categoryLabel = caseCategory } = {} } = {},
    },
  } = categoryMap;

  return categoryLabel;
};

type RT = Feedback.CaseBasicInfo;

export interface FeedbackTableProps extends ScrollTableProps<RT> {
  onCheckDetail: (record: RT) => void;
  categoryMap: CategoryMap;
}

function FeedbackTable(props: FeedbackTableProps) {
  const { onCheckDetail, categoryMap, ...otherProps } = props;

  const { t } = useTranslate();

  const systemLanguage = useSystemLanguageValue();

  const columns = useMemo<TableColumnsType<RT>>(() => {
    return [
      {
        key: 'id',
        dataIndex: 'caseId',
        title: 'ID',
        width: 80,
        fixed: 'left',
      },
      {
        key: 'message',
        dataIndex: 'message',
        title: t('feedback.内容'),
        width: 250,
        render: (message: RT['message']) => {
          try {
            const { messageType, textContent, imageContent, imageWithTextContent } = JSON.parse(
              message,
            ) as Conversation.Message;

            if (messageType === 'TEXT') {
              const text = textContent?.originalText || textContent?.text;

              return (
                <Typography.Paragraph style={{ marginBottom: 0 }} ellipsis={{ rows: 2 }}>
                  {text}
                </Typography.Paragraph>
              );
            }

            if (messageType === 'IMAGE_WITH_TEXT') {
              const { text = '' } = imageWithTextContent || {};

              return (
                <Typography.Paragraph style={{ marginBottom: 0 }} ellipsis={{ rows: 2 }}>
                  {`[${t('messageType.IMAGE')}]${text}`}
                </Typography.Paragraph>
              );
            }

            if (messageType === 'IMAGE') {
              const src = imageContent?.externalImgUrl || imageContent?.imgUrl;

              return <Image src={src} alt="messageImage" />;
            }

            return t({
              key: `messageType.${messageType}`,
              defaultValue: '-',
            });
          } catch (err) {
            return '-';
          }
        },
      },
      {
        key: 'caseType',
        dataIndex: 'caseType',
        title: t('feedback.类型'),
        width: 150,
        render: (caseType: RT['caseType']) => {
          return {
            UN_ANSWERED: <BlueTag>{t('feedback.未识别')}</BlueTag>,
            WRONG_ANSWER: <OrangeTag>{t('feedback.回复错误')}</OrangeTag>,
            ACTIVITY: <BlueTag>活动反馈</BlueTag>,
          }[caseType];
        },
      },
      {
        key: 'caseCategory',
        dataIndex: 'caseCategory',
        title: t('feedback.分类'),
        width: 160,
        render: (_, record: RT) => {
          if (record.caseType === 'ACTIVITY') {
            return t('feedback_of_activity');
          }

          return getCategoryLabel(record, categoryMap, systemLanguage);
        },
      },
      {
        key: 'caseStatus',
        dataIndex: 'caseStatus',
        title: t('feedback.最新状态'),
        width: 150,
        render: (caseStatus: RT['caseStatus']) => {
          return {
            NEW: t('feedback.修复中'),
            PROCESSING: t('feedback.修复中'),
            TESTING: t('feedback.修复中'),
            FIXED: t('feedback.已修复待确认'),
            CLOSED: t('feedback.已关闭'),
          }[caseStatus];
        },
      },
      {
        key: 'feedback',
        dataIndex: 'feedback',
        title: t('feedback_resubmit'),
        width: 150,
        render: (feedback: RT['feedback']) => {
          return feedback ? t('marketing.是') : t('marketing.否');
        },
        filterMultiple: false,
        filters: [
          {
            text: t('marketing.是'),
            value: 1,
          },
          {
            text: t('marketing.否'),
            value: 2,
          },
        ],
      },
      {
        key: 'assistantName',
        dataIndex: 'assistantName',
        title: t('feedback.提交客服'),
        width: 150,
      },
      {
        key: 'createTime',
        dataIndex: 'createTime',
        title: t('feedback.提交时间'),
        width: 200,
        render: (createTime: RT['createTime']) => dayjs(createTime).format('YYYY-MM-DD HH:mm:ss'),
      },
      {
        key: 'operation',
        title: t('操作'),
        width: 100,
        fixed: 'right',
        render: (_, record: RT) => {
          return (
            <DetailButton type="link" onClick={() => onCheckDetail(record)}>
              {t('tableColumn.查看详情')}
            </DetailButton>
          );
        },
      },
    ];
  }, [onCheckDetail, systemLanguage, categoryMap, t]);

  return <ScrollTable rowKey="caseId" columns={columns} {...otherProps} />;
}

export default FeedbackTable;
